<template>
<div>
  <!-- CARDS -->
  <div class="row">
    <div v-for="(car, index) in cars" :key="index" class="mb-4 col-12 col-sm-6 col-md-6 col-lg-4 col-xl-4">
      <div class="card shadow h-100 py-2">
        <div class="card-body">
          <div class="row no-gutters align-items-center">
            <div class="col mr-2">
              <div class="text-xs font-weight-bold text-alert text-uppercase mb-1"> 
                <!-- Show connected -->
                <span v-if="show_connected(car)" style="color: green">
                  {{ $t('Car_connected') }} 
                  <span class="text-gray-600" v-if="car.event.typology"> 
                    ({{ car.event.typology == 1 ? $t('Single_phase') : $t('Three_phase') }})
                  </span>
                </span>
                <span v-else>{{ $t('Car_not_connected') }}</span>
              </div>
              <!-- Show not Charging -->
              <div v-if="show_not_charging(car)" 
                class="text-xs font-weight-bold text-alert text-uppercase mb-1"> 
                <span>{{ $t('Not_charging') }}</span>
              </div>
              <div v-else>
                <div class="text-sm font-weight-bold text-uppercase mb-1" :style="{'color': 'green'}"> 
                  <span class="badge badge-green">{{ $t('Charging') }}</span>
                </div>
                <div class="h5 mb-0 font-weight-bold text-gray-800">
                  <span>
                    {{ 
                      format_thousands(
                        Math.round(car.data.L1.active_power || 0) + Math.round(car.data.L2.active_power || 0) + Math.round(car.data.L3.active_power || 0)
                      ) 
                    }} W
                  </span>
                </div>
              </div>
            </div>
            <div class="col-auto">
              <i class="fas fa-charging-station fa-3x"></i>
              <span class="h1 ml-2 font-weight-bold">{{ car.station.ev.number }}</span>
            </div>
          </div>
          
          <!-- CHARGING ACTIONS -->
          <div class="row mt-3">
            <div v-if="user_allowed && !error_in_equipment(car) && car.data.ev_state < 4" class="col-12 text-center">
              <p>{{ $t('Type_of_charge') }}</p>
              <!-- Normal charge button -->
              <button @click="setChargingType(car, false)"
                :class="{'btn-light': car.event.smart_charging, 'btn-primary': !car.event.smart_charging}" class="btn mr-1">
                {{ $t('Normal') }}
              </button>
              <!-- Smart charge button -->
              <button @click="setChargingType(car, true)"
                :class="{'btn-light': !car.event.smart_charging, 'btn-primary': car.event.smart_charging}" class="btn">
                {{ $t('Smart') }}
              </button>
            </div>
            <div v-else-if="!is_EEM" class="col-12 text-center">
              <!-- Finish button -->
              <button v-show="show_end_button(car)" 
                @click="stop(car)" class="btn btn-sm btn-danger">
                <i class="fas fa-stop-circle fa-fw"></i> {{ $t('Finish') }}
              </button>
              <!-- Schedule button -->
              <button
                v-show="show_schedule_button(car)" 
                class="btn btn-sm btn-light ml-1" @click="schedule(car)">
                <i class="fas fa-stopwatch fa-fw"></i> {{ $t('Schedule') }} 
              </button>
              <!-- Charging mode button -->
              <span v-if="show_charging_mode_button(car)">
                <button class="btn btn-sm btn-secondary ml-1" type="button" @click="open_charging_mode(car)">
                  <i class="fas fa-cog"></i> 
                  {{ $t('Charging_mode') }}
                </button>
                <span class="mt-1 d-block">{{ $t('Charging_in_mode') }}:
                  <span class="font-weight-bold">{{ car.event.charging_rate ? $t('Manual') : $t('Automatic') }} </span>
                  <span class="font-weight-bold" v-if="car.event.charging_rate"> 
                    {{ pwm_to_percentage(car) }}%
                  </span>
                </span>
              </span>
              <!-- Start & End Messages -->
              <div v-show="(!car.event.charge_end_button && !check_charging_end(car))" class="col-xl-12 text-center mt-2">
                <span v-if="show_start_message(car) && !error_in_equipment(car)" class="d-block">
                  {{ $t('Charging_will_start')}} {{ $t('at') }}: <strong> {{ formatDate(car.event.charge_start_timestamp) }} </strong>
                </span>
                <span v-if="show_end_message(car) && !error_in_equipment(car)" class="d-block">
                 {{ $t('Charging_will_finish')}} {{ $t('at') }}: <strong> {{ formatDate(car.event.charge_end_timestamp) }} </strong>
                </span>
              </div>
              <!-- Finishing soon Message -->
              <div v-show="show_finishing_message(car) && !error_in_equipment(car)">
                <span> {{ $t('Charging_will_finish') }} {{ $t('in_the_next_few_seconds') }}.</span>
              </div>
            </div>
          </div>

          <!-- WARNINGS -->
          <div class="row text-center">
            <span class="alert alert-info text-uppercase mt-1 mb-1 d-block mx-auto"
              v-if="(car.data.ev_state==2 && (car.event.typology == 1 || car.event.typology == 3))">
              <i class="fas fa-exclamation-circle"></i>
              {{ $t('Remove_vehicle_from_station') }}
            </span>
            <span @click="open_instructions(car)" v-if="(car.data.ev_state >= 4 || error_in_equipment(car))" 
              class="alert alert-danger text-uppercase mb-1 red d-block mx-auto">
              <i class="fas fa-exclamation-triangle"></i>
              <span style="cursor:pointer;" v-if="error_in_equipment(car)"> 
                {{ $t('Error_in_equipment') }}
              </span>
              <span v-if="(car.data.ev_state == 5)"> {{ $t("Vehicle_error") }} - {{ $t('Remove_vehicle_from_station') }}</span>
              <span v-if="(car.data.ev_state == 4)"> {{ $t("Station_error") }} - {{ $t('Remove_vehicle_from_station') }}</span>
            </span>
          </div>

        </div>
      </div>
    </div>
  </div>

  <!-- The Modal -->
  <div class="modal" id="myModal">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            <span v-if="modal_type=='instructions'">{{ $t('Instructions') }}</span>
            <span v-if="modal_type=='schedule'">{{ $t('Schedule') }} {{ $t('charging') }}</span> 
            <span v-if="modal_type=='charging_mode'">{{ $t('Charging_mode') }}</span>
            <span v-if="modal_type!='instructions'">- {{ $t('Car') }} {{ selected_car.station.ev.number }}</span>
          </h4>
          <button style="color: white" type="button" class="close" data-dismiss="modal">&times;</button>
        </div>
        <div class="modal-body">
          <!-- Schedule mode -->
          <div v-show="modal_type=='schedule'" class="form">
            <div v-show="selected_car.data.ev_state !=3" class="form-group row">
              <label class="col-2 col-form-label">{{ $t('Start') }}:</label>
              <div class="input-group col-10">
                <label class="input-group-addon btn">
                  <span class="fa fa-clock"></span>
                </label>
                <input type="text" class="form-control" id="datetime-picker-start" value=""/>
              </div>
            </div>
            <div class="form-group row">
              <label class="col-2 col-form-label">{{ $t('End') }}:</label>
              <div class="input-group col-10">
                <label class="input-group-addon btn">
                  <span class="fa fa-clock"></span>
                </label>
                <input type="text" class="form-control" id="datetime-picker-end" value=""/>
              </div>
            </div>
            <div class="row">
              <div class="col-12 text-center">
                <button @click="confirmSchedule(selected_car)" class="btn btn-sm btn-success">
                  <i class="fas fa-check fa-fw"></i> {{ $t('Confirm_schedule') }} 
                </button>
              </div>
            </div>
          </div>
          <!-- Charging mode -->
          <div v-show="modal_type=='charging_mode'" class="row">
            <div class="col-12 text-center mb-2">
              <button 
                type="button" 
                :class="{'btn-primary': (temp_duty_cycle == null || temp_duty_cycle < 0), 'btn-light': (temp_duty_cycle != null && temp_duty_cycle >= 0) }" 
                @click="change_charging_mode(selected_car, 'auto')"
                class="btn ">
                {{ $t('Automatic') }}
              </button>
              <button 
                type="button" 
                :class="{'btn-primary': (temp_duty_cycle != null && temp_duty_cycle >= 0), 'btn-light': (temp_duty_cycle < 0 || temp_duty_cycle == null) }" 
                @click="change_charging_mode(selected_car, 'manual')" 
                class="btn ml-1">
                {{ $t('Manual') }}
              </button>
            </div>
            <!-- Info -->
            <div class="container">
              <div class="col-12 text-center" style="border-top: 1px solid lightgrey; border-bottom: 1px solid lightgrey">
                <span class="text-gray-800">
                  <i class="fas fa-home"></i>
                  {{ $t('Total_available_power') }}
                </span>
                <p v-if="available_power!=null">
                  {{ available_power }} W 
                  <span v-show="temp_duty_cycle != null && temp_duty_cycle >=0 && available_power_manual != null">
                    <i class="fas fa-angle-double-right"></i>
                    <span :class="{'red': available_power_manual < 0 }">
                      {{ available_power_manual }} W
                    </span>
                  </span>
                </p>
                <p v-else>{{ $t('No_information') }}</p>
              </div>
              <div v-show="temp_duty_cycle != null && temp_duty_cycle >= 0" class="row mt-1 mb-3 text-center" style="border-bottom: 1px solid lightgrey">
                <div class="col-6" style="border-right: 1px solid lightgrey"> 
                  <span class="text-gray-800">
                    <i class="fas fa-charging-station"></i>
                    <span class="d-block"> {{ $t('Station_max_power') }} </span>
                  </span>
                  <p>{{ selected_car.event.typology == 3 ? selected_car.station.ev.max_power_tri : selected_car.station.ev.max_power_single }} W </p>
                </div>
                <div class="col-6" > 
                  <span class="text-gray-800">
                    <i class="fas fa-car"></i>
                    <span class="d-block">{{ $t('Power_to_apply_vehicle') }}</span>
                  </span>
                  <p> {{ power_used }} W </p>
                </div>
              </div>
              <div v-show="available_power_manual < 0" class="rpw alert alert-danger text-center" role="alert">
                {{ $t("Not_enough_power") }}
              </div>
            </div>
            <!-- Charge rate slider -->
            <div v-show="temp_duty_cycle != null && temp_duty_cycle >= 0" class="col-12 mt-1 mb-3 text-center">
              <span>{{ $t('Select_power_of_charge') }} (%):</span>
              <vue-slider class="mt-4" v-model="temp_duty_cycle" 
                :height="20" 
                :tooltip="'always'" 
                :tooltip-placement="'top'" 
                :marks="marks" 
                :included="true"
                :adsorb="true" 
                :tooltip-formatter="formatter" 
                :interval="1">
              </vue-slider>
            </div>
            <div v-show="temp_duty_cycle < 0 || temp_duty_cycle == null" class="col-12 mt-1 text-center">
              <span>{{ $t('Charging_will_be_done_automatically') }}</span>
            </div>
            <div v-show="pwm_to_percentage(selected_car) != temp_duty_cycle" class="col-12 text-center">
              <button 
                :disabled="available_power_manual < 0" 
                @click="set_charging_rate(selected_car)" class="btn btn-sm btn-success mt-4"> 
                {{ $t('Confirm') }}
              </button>
            </div>
          </div>
          <!-- Instructions mode -->
          <div v-show="modal_type=='instructions'" class="row">
            <div class="col-12 text-left mb-2">
              <div v-if="(selected_car.data.ev_state == 5) || ((Object.keys(selected_car.data).length === 0 && selected_car.data.constructor === Object) || selected_car.data.ev_state == null)">
                <p>
                  <strong v-if="(selected_car.data.ev_state == 5)">{{ $t('Station_error') }}:</strong>
                  <strong v-else>{{ $t('Error_in_equipment') }}</strong>
                </p>
                <p> {{ $t('Check_if_equipment_off') }} <u>{{ $t('disconnected_from_power') }}</u> {{ $t('and_connect_it') }}. </p>
                <p> {{ $t('Otherwise_follow_steps') }}: </p>
                <ol>
                  <li>{{ $t('Remove_vehicle_from_station') }}.</li>
                  <li>{{ $t('Open_box_shutdown_circuit_wait') }}.</li>
                  <li>{{ $t('Turn_circuit_wait') }}.</li>
                  <li>{{ $t('Connect_vehicle_to_station') }}.</li>
                </ol>
                <p>{{ $t('Problem_persists_contact') }} 934 535 790.</p>
              </div>
              <div v-if="(selected_car.data.ev_state == 4)">
                <p><strong>{{ $t('Vehicle_error') }}:</strong></p>
                <ol>
                  <li>{{ $t('Remove_vehicle_from_station') }}.</li>
                  <li>{{ $t('Contact_vehicle_supplier') }}.</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" @click="closeModal" class="btn btn-sm btn-danger" data-dismiss="modal">{{ $t('Cancel') }}</button>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'

export default  {
    name: 'EVCurrentData',
    components: {
      VueSlider,
    },
    created() {
      this.$socket.io.opts.query = {
        producer_id: this.$store.getters.user_id,
        jwt: this.$store.getters.token
      }
      this.$socket.close()
      this.$socket.open()
    },
    mounted() {
      const datetime_picker_options = {
        format: 'DD-MM-YYYY HH:mm:[00]',
        viewMode: 'days',
        sideBySide: true,
        ignoreReadonly: true,
        locale: moment.locale(this.current_lang, {
          week: { dow: 1 }
        }),
        useCurrent: false,
        toolbarPlacement:'top',
        debug: false,
        showClear:true,
        showClose:true,
        icons: {
          previous: "fas fa-chevron-left",
          next: "fas fa-chevron-right",
          up: "fas fa-chevron-up",
          down: "fas fa-chevron-down",
          time: "fas fa-clock",
          date: 'fas fa-calendar',
          clear: 'fas fa-trash',
          close: 'fas fa-window-close'
        }
      }

      $(`#datetime-picker-start`).datetimepicker(datetime_picker_options)
      $(`#datetime-picker-end`).datetimepicker(datetime_picker_options)

      $('#myModal').on('hidden.bs.modal', function(){
        $(`#datetime-picker-start`).data("DateTimePicker").clear()
        $(`#datetime-picker-end`).data("DateTimePicker").clear()
        this.temp_duty_cycle = null
      })
    },
    data() {
      return {
        cars: [],
        set_data_flag: true,
        marks: [],
        selected_car: { 
          station: { ev: { num: null, max_power: null } }, 
          event: {}, 
          data: { consumption: null, ev_connect: null, ev_state: null } 
        },
        formatter: '{value}%',
        modal_type: "schedule",
        temp_duty_cycle: null,
      }
    },
    sockets: {
      connect: function () {
        console.log('socket connected')
      },
      data: function(data) {
        //console.log("data received from socket", data.producerId)
        let car_data = null
        if (this.$store.getters.user_isAdmin) {
          if (this.$store.getters.selected_producerId != data.producerId) return
          car_data = data.data
        } 
        else car_data = data
        
        //console.log(car_data)
        car_data.forEach(d => {
          let car = this.cars.find(c => c.station.deviceId == d.device_id)
          if (car && car.hasOwnProperty('data')) {
            car.event = d.data.event
            car.data = d.data.data
          }
        })
      }
    },
    methods: {
      formatDate: function(date){
        return moment(date).format("DD-MM-YYYY HH:mm")
      },
      dateHasPassed: function(date){
        return (new Date()).valueOf() >= (new Date(date)).valueOf()
      },
      closeModal: function(){
        $("#myModal").modal('hide')
        $("#datetime-picker-start").data("DateTimePicker").clear()
        $("#datetime-picker-end").data("DateTimePicker").clear()
        this.temp_duty_cycle = null
      },
      pwm_to_percentage: function(car) {
        let percentage = null
         const station_max_power = car.event.typology == 3 ? car.station.ev.max_power_tri : car.station.ev.max_power_single
        if (car.event.charging_rate) 
          percentage = Math.floor((car.event.charging_rate*230*1*60*car.event.typology)/station_max_power)
        else 
          percentage = Math.floor((this.charging_rate_maximum(car) + this.charging_rate_minimum(car))/2)
        return percentage
      },
      setChargingType: function(car, bool){
        _paq.push(['trackEvent', this.current_page, `Carregamento ${bool ? 'Inteligente' : 'Normal'}`, "EV_"+car.station.ev.number])
        /* car.event = { ...car.event, smart_charging: bool}
        console.log("smart charging", car.event.smart_charging) */
        this.$store.dispatch('setChargingType', { 
          deviceId: car.station.deviceId,
          bool
        }).then((ev_data) => {
          car.event = ev_data
        })
      },
      open_charging_mode: function(car){
        this.selected_car = car
        //this.temp_duty_cycle = this.pwm_to_percentage(car)
        this.modal_type = 'charging_mode'
        _paq.push(['trackEvent', this.current_page, 'Modo de carregamento', "EV_"+car.station.ev.number])
        $("#myModal").modal('show')
      },
      open_instructions: function(car) {
        this.modal_type = 'instructions'
        this.selected_car = car
        _paq.push(['trackEvent', this.current_page, 'Ver Instruções', "EV_"+car.station.ev.number])
        $("#myModal").modal('show')
      },
      schedule: function(car){
        this.modal_type = 'schedule'
        this.selected_car = car 

        if(car.event.charge_start_timestamp){
          $("#datetime-picker-start").data("DateTimePicker").date(new Date(car.event.charge_start_timestamp))
        }  
        
        if(car.event.charge_end_timestamp){
          $("#datetime-picker-end").data("DateTimePicker").date(new Date(car.event.charge_end_timestamp))
        }
        _paq.push(['trackEvent', this.current_page, 'Agendar', "EV_"+car.station.ev.number]) 
        $("#myModal").modal('show')
      },
      confirmSchedule: function(car){
        const deviceId = car.station.deviceId
        const datetime_start = $(`#datetime-picker-start`).data("DateTimePicker").date()
        const datetime_end = $(`#datetime-picker-end`).data("DateTimePicker").date()

        if (datetime_start == null && datetime_end == null) return

        _paq.push(['trackEvent', this.current_page, 'Confirmar agendamento', "EV_"+car.station.ev.number])
        
        this.$store.dispatch('scheduleCharging', { 
          deviceId,
          start: datetime_start ? datetime_start.toJSON() : null, 
          end: datetime_end ? datetime_end.toJSON() : null,
        }).then((ev_data) => {
          car.event = ev_data
          $("#myModal").modal('hide')
        })
      },
      stop: function(car){
        const deviceId = car.station.deviceId
        _paq.push(['trackEvent', this.current_page, 'Terminar carregamento', "EV_"+car.station.ev.number])
        this.$store.dispatch('stopCharging', deviceId).then((ev_data) => {
          car.event = ev_data
        })
      },
      check_charging_end: function(car){
        const time_diff = car.event.charge_end_timestamp && (new Date(car.event.charge_end_timestamp)).valueOf() - (new Date()).valueOf()
        if (
          (car.event.charge_end_button && !car.event.charge_end_button_sent) ||
          (car.event.charge_end_timestamp && (time_diff < 60000 && time_diff > 0) && !car.event.charge_end_timestamp_sent)
        ) return true
        else return false     
      },
      change_charging_mode: function(car, mode){
        _paq.push(['trackEvent', this.current_page, 'Alterar modo de carregamento', "EV_"+car.station.ev.number+" ("+mode+")"])
        if (mode == "manual") 
          this.temp_duty_cycle = this.pwm_to_percentage(car) //Math.round(((this.charging_rate_minimum(car)+this.charging_rate_maximum(car))/2))
        else if (mode == "auto") 
          this.temp_duty_cycle = null
      },
      set_charging_rate: function(car){
        const deviceId = car.station.deviceId
        this.$store.dispatch('scheduleCharging', { 
          deviceId,
          start: null, 
          end: null,
          charging_rate: this.calculate_duty_cycle(car)
        }).then((ev_data) => {
          car.event = ev_data
          $("#myModal").modal('hide')
        })
      },
      charging_rate_minimum: function(car) {
        const station_max_power = car.event.typology == 3 ? car.station.ev.max_power_tri : car.station.ev.max_power_single
        const minimum = (138000 * car.event.typology)/station_max_power
        return Math.ceil(minimum)
      },
      charging_rate_maximum: function(car) {
        const station_max_power = car.event.typology == 3 ? car.station.ev.max_power_tri : car.station.ev.max_power_single
        const maximum = ((car.station.ev.max_power_single*100) * car.event.typology)/station_max_power
        return Math.floor(maximum)
      },
      calculate_duty_cycle: function(car) {
        const V = 230
        const PF = 1
        const station_max_power = car.event.typology == 3 ? car.station.ev.max_power_tri : car.station.ev.max_power_single
        if (this.temp_duty_cycle == null) return null
        
        const duty_cycle = (this.temp_duty_cycle * station_max_power)/(V*PF*60*car.event.typology)
        return duty_cycle
      },
      format_thousands: function(n){
        return n.toLocaleString()
      },
      power_exceeds_house: function(max_power) {
        return this.available_power < this.power_used
      },
      show_schedule_button: function(car) {
        return (
          car.data.ev_state != 4 && car.data.ev_state != 5 && car.data.ev_state != null &&
          !car.event.end_charge_button &&
          car.data.ev_state != 2 &&
          (Object.keys(car.data).length > 0 && car.data.constructor === Object)
        )
      },
      show_charging_mode_button: function(car) {
        const station_max_power = car.event.typology == 3 ? car.station.ev.max_power_tri : car.station.ev.max_power_single
        const max_reference = car.event.typology == 3 ? 20700 : 6900
        const max_duty_cycle = Math.ceil(station_max_power * 50 / max_reference)
        return this.user_allowed && car.data.ev_state == 3 && car.event.typology && car.event.duty_cycle >= 10 && car.event.duty_cycle <= max_duty_cycle
      },
      show_end_button: function(car) {
        const station_max_power = car.event.typology == 3 ? car.station.ev.max_power_tri : car.station.ev.max_power_single
        const max_reference = car.event.typology == 3 ? 20700 : 6900
        const max_duty_cycle = Math.ceil(station_max_power * 50 / max_reference)
        return ( 
          car.data.ev_connect && 
          car.data.ev_state==3 && 
          car.event.typology &&
          !car.event.charge_end_button  &&
          (car.event.duty_cycle >= 10 && car.event.duty_cycle <= max_duty_cycle)
        )
      },
      show_start_message: function(car) {
        if (
          (car.event.charge_start_timestamp && !car.event.charge_start_timestamp_sent) || 
          (car.event.charge_start_timestamp && !this.dateHasPassed(car.event.charge_start_timestamp))
        )
        return true 
        else return false
      },
      show_end_message: function(car) {
        if (
          (car.event.charge_end_timestamp && !car.event.charge_end_timestamp_sent) || 
          (car.event.charge_end_timestamp && !this.dateHasPassed(car.event.charge_end_timestamp))
        )
        return true 
        else return false
      },
      show_finishing_message: function(car) {
        if (
          (car.data.ev_connect == 1 && car.data.ev_state == 3 && this.check_charging_end(car)) || 
          (car.event.charge_end_button && !car.event.charge_end_button_sent) || 
          (car.event.charge_end_timestamp && !car.event.charge_end_timestamp_sent && this.dateHasPassed(car.event.charge_end_timestamp))
        ){
          return true
        } else return false
      },
      show_connected: function(car) {
        return car.data.ev_connect == 1
      },
      show_not_charging: function(car) {
        return car.data.ev_state != 3 || !car.event.typology || car.event.duty_cycle == 100 || (car.event.duty_cycle == 0 && car.event.duty_cycle != null)
      },
      error_in_equipment: function(car) {
        return ((Object.keys(car.data).length === 0 && car.data.constructor === Object) || car.data.ev_state == null)
      },
      isToday: function(date) {
        const today = new Date()
        return date.getDate() == today.getDate() &&
          date.getMonth() == today.getMonth() &&
          date.getFullYear() == today.getFullYear()
      }
    },
    computed: {
      power_used: function(){
        const station_max_power = this.selected_car.event.typology == 3 ? this.selected_car.station.ev.max_power_tri : this.selected_car.station.ev.max_power_single
        return Math.round(station_max_power*(this.temp_duty_cycle/100))
      },
      data_total: function(){
        return this.$store.getters.data_total_group
      },
      ev_data: function(){
        return this.$store.getters.data
      },
      available_power: function(){
        const V = 230
        const PF = 1
        const power_safety = 5 * V
        const house_max_power = this.$store.getters.user_contract.kVA * PF * 1000 || 0
        let house_consumption = 0

        if (this.data_total.summaryChart && this.data_total.summaryChart.length) 
          house_consumption = this.data_total.summaryChart.slice(-1)[0].consumption || 0

        const available_power = Math.round(house_max_power - house_consumption - power_safety) //+ cars_total_power

        return available_power
      },
      available_power_manual: function(){
        let car_consumption = 0
        if (this.selected_car.data.hasOwnProperty('active_power') && this.selected_car.data.active_power) 
          car_consumption = this.selected_car.data.active_power

        return this.available_power + car_consumption - this.power_used
      },
      manual_cars: function(){
        return this.cars.filter(car => car.charging_rate != null)
      },
      automatic_cars: function(){
        return this.cars.filter(car => car.charging_rate == null)
      },
      is_EEM: function(){
        return this.$store.getters.user_id == "5ad5dd1177a88f0656ff4077"
      },
      user_allowed: function(){
        return (this.$store.getters.user_id == "5ad5dd1177a88f0656ff4077" || this.$store.getters.user_isAdmin)
      },
      colors: function(){
        return this.$store.getters.ev_colors
      },
      current_lang: function(){
        return this.$i18n.locale
      },
      timezone: function(){
        return this.$store.getters.timezone
      },
      range: function(){
        return this.$store.getters.range
      },
      current_page: function() {
        return this.$route.name
      },
      selected_producerId: function() {
        return this.$store.getters.selected_producerId
      },
    },
  watch: {
    selected_producerId: function() {
      this.set_data_flag = true

    },
    ev_data: function(){
      if (!this.set_data_flag || this.ev_data.length == 0) return null
      
      this.cars = []
      this.ev_data.forEach(d => {
        this.cars.push({
          station: d.device,
          data: { ...d.current_data, active_power: 19500 || d.current_data.consumption },
          event: d.events,
          duty_cycle: null,
        })
      })
      this.set_data_flag = false
    },
    selected_car: {
      handler: function() {
        this.value = Math.round((this.charging_rate_minimum(this.selected_car)+this.charging_rate_maximum(this.selected_car))/2)
        const slider_max = this.charging_rate_maximum(this.selected_car)
        const slider_min = Math.max(this.charging_rate_minimum(this.selected_car), 1)
        this.marks = val => {
          if (val < slider_min || val > slider_max) return false
          else { 
            if (val == slider_min || val == slider_max)
          		return true
            else {
              return ({ 
                labelStyle: { opacity: 0 }, 
              })
            }
          } 
        } 
      },
      deep: true
    },
    current_lang: function(){
      $("#datetime-picker-start").data("DateTimePicker").options({
        locale: moment.locale(this.current_lang, {
          week: { dow: 1 }
        })
      })
      $("#datetime-picker-end").data("DateTimePicker").options({
        locale: moment.locale(this.current_lang, {
          week: { dow: 1 }
        })
      })
    }
  }
}
</script>

<style>
@import '../assets/vendor/datetimepicker/css/bootstrap-datetimepicker.min.css'; 

.red {
  color: red;
}

.btn-light {
  background-color: #dddddd;
}

.modal-header {
  background-color: #333333;
  color: white;
}

.modal-footer {
  background-color: #f0f1f5;
  color: white;
}

.vue-slider-marks .vue-slider-mark-step {
  background-color: transparent !important;
}

.badge-green {
  background-color: green;
  color: white;
}

.green-dot {
  height: 10px;
  width: 10px;
  background-color: green;
  border-radius: 50%;
  display: inline-block;
}
</style>
